// pkg68 — Firm Billing page (SETTINGS): the LawMighty subscription in one place — plan/status/renewal, Subscribe (owner or purchase_billing staff), Manage billing → Stripe Customer Portal (owner-only, hidden otherwise); receives ?billing=success|cancelled from Checkout (D-68e) — delivered 2026-07-12
//
// "Firm Billing" is what LawMighty charges the FIRM — completely separate
// from the BILLING nav section (Log Time / Invoices / Trust), which is the
// firm billing its CLIENTS. Credits live next door on Firm Wallet.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';

const META = 'Arial, sans-serif';

export default function AdminFirmBillingPage() {
  const [billing, setBilling] = useState(null);
  const [err, setErr]         = useState('');
  const [msg, setMsg]         = useState('');
  const [payBusy, setPayBusy] = useState('');

  useEffect(() => {
    const flag = new URLSearchParams(window.location.search).get('billing');
    if (flag === 'success')   setMsg('Subscription started — welcome aboard! It can take a few seconds to activate.');
    if (flag === 'cancelled') setMsg('Checkout was cancelled — nothing was charged.');

    let cancelled = false;
    let tries = 0;
    const load = () => adminApi.getBillingStatus()
      .then(b => {
        if (cancelled) return;
        setBilling(b);
        // Webhook lag: after a success return, poll briefly until the
        // subscription shows active (same pattern as the pkg65 card).
        if (flag === 'success' && !b.subscribed && tries < 6) {
          tries += 1;
          setTimeout(load, 2000);
        }
      })
      .catch(e => { if (!cancelled) setErr(e.message); });
    load();

    return () => { cancelled = true; };
  }, []);

  async function action(kind) {
    setErr(''); setPayBusy(kind);
    try {
      const call = kind === 'subscribe' ? adminApi.createSubscriptionCheckout
                 :                        adminApi.openBillingPortal;
      const { url } = await call();
      window.location.href = url;
    } catch (e) {
      setErr(e.message || 'Could not open Stripe.');
      setPayBusy('');
    }
  }

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return (
    <div>
      <h1 className="page-title">Firm Billing</h1>
      <p className="page-subtitle">
        Your firm's LawMighty subscription — plan, renewal, payment method, and receipts. AI credits
        live on <Link to="/admin/dashboard/firm-wallet">Firm Wallet</Link>.
      </p>

      <div className="card">
        {msg && <div className="alert alert-success">{msg}</div>}
        {err && <div className="alert alert-error">{err}</div>}
        {!billing && !err && (
          <p style={{ fontFamily: META, fontSize: 13, color: '#888', margin: 0 }}>Loading billing…</p>
        )}

        {billing && billing.exempt && (
          <p style={{ fontFamily: META, fontSize: 13, color: '#333', margin: 0 }}>
            <strong>Complimentary plan.</strong> LawMighty is provided to your firm at no charge —
            all features and AI actions included.
          </p>
        )}

        {billing && !billing.exempt && (
          <>
            <p style={{ fontFamily: META, fontSize: 13, color: '#333', marginTop: 0, marginBottom: 12 }}>
              {billing.subscribed && billing.subscription_status !== 'past_due' && (
                <><strong>LawMighty subscription — $29.99/month, unlimited users.</strong>
                {billing.current_period_end ? <> Renews {fmtDate(billing.current_period_end)}.</> : null}</>
              )}
              {billing.subscription_status === 'past_due' && (
                <><strong style={{ color: '#a15c00' }}>Payment problem.</strong> Your last payment failed — we'll
                retry automatically. Please update your card in "Manage billing" to keep uninterrupted access.</>
              )}
              {!billing.subscribed && !billing.locked && (
                <><strong>Free trial{billing.trial_ends_at ? <> — ends {fmtDate(billing.trial_ends_at)}</> : null}.</strong>{' '}
                Subscribe any time to keep everything going: $29.99/month, unlimited users.</>
              )}
              {!billing.subscribed && billing.locked && (
                <><strong>Your trial has ended.</strong> Everything is saved — subscribe to restore access
                for your whole firm: $29.99/month, unlimited users.</>
              )}
            </p>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {!billing.subscribed && (
                <button type="button" className="btn btn-primary" disabled={!!payBusy}
                  onClick={() => action('subscribe')}>
                  {payBusy === 'subscribe' ? <><span className="spinner" />Opening checkout…</> : 'Subscribe — $29.99/month'}
                </button>
              )}
              {billing.has_customer && (
                <button type="button" className="btn" disabled={!!payBusy}
                  onClick={() => action('portal')}>
                  {payBusy === 'portal' ? <><span className="spinner" />Opening…</> : 'Manage billing'}
                </button>
              )}
            </div>
            <p style={{ fontFamily: META, fontSize: 12, color: '#888', marginTop: 10, marginBottom: 0 }}>
              Secure payments by Stripe. "Manage billing" covers card updates, invoices/receipts, and
              cancellation (access runs through the period you've paid for). Card and plan changes are owner-only.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
